/* eslint-disable sonarjs/cognitive-complexity */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Template, TemplateScope } from '@dev/ee-api/ee-api-models';
import { safelyDecodeBase64Url, safelyEncodeBase64Url } from '@dev/ee-tools';
import { Container, DestructiveButton, PrimaryButton, SimpleLoader, Steps, TabsPanel } from '@dev/ee-components';
import { useExitModal } from '@/services/hooks/useExitModal';
import { CheckoutsPaths, pathsParams } from '@/services/dictionaries/Paths';
import { GetContactFieldsStart, getCustomFieldsStore } from '@/store/customFields/actions';
import { HistoryBackActionContext, useHistoryBackAction } from '@/services/hooks/useHistoryBackAction';
import { getValidEmailsStore, LoadValidEmails } from '@/store/validemails/actions';
import { getDomainsStore, LoadDomains } from '@/store/domain/actions';
import { getCheckoutCreatorStore, LoadStart, Reset, SetIsTemplateOpen, SetTemplatePreview, TemplateFormsSetDefaultDoubleOptInDisabled, TemplateSetFromGallery, UpdateTemplatesSaveSettings, TemplateOpenFromGallery } from '@/store/checkoutCreator/actions';
import { Reset as TemplateEditorReset } from '@/store/templateEditor/actions';
import { MergeFieldsLoadStart, getMergeFieldsStore } from '@/store/mergeFields/actions';
import { MergeFieldsStateStatus } from '@/store/mergeFields/types';
import { ListsGetNamesStart } from '@/store/lists/actions';
import { useCurrentPlanStatus } from '@/services/hooks/useCurrentPlanStatus';
import { CheckoutCreatorStatus } from '@/store/checkoutCreator/types';
import { LoadLandingPageSubdomain } from '@/store/landingPageSubdomain/actions';
import { CheckoutsStripeAccountFetch } from '@/store/checkoutsStripeAccount/actions';
import { TemplateEditorModal } from './components/TemplateEditorModal';
import { WebsiteModePreviewOverlay } from '../TemplatePreviewComponent';
import { useSteps } from './hooks/useSteps';
import { useLandingPageSubdomain } from './hooks/useLandingPageSubdomain';
import { EmailTemplateEditorModal } from './components/EmailTemplateEditorModal';
import { DEFAULT_TEMPLATE_EDITOR_META_DATA } from './constants';
import { getCheckoutCreatorGalleryOpenStore } from '@/store/checkoutCreator/actions';
import { getCheckoutsStore } from '@/store/checkouts/actions';

export const CheckoutCreator = (): JSX.Element => {
    const [isGalleryCreator, setIsGalleryCreator] = useState(false);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { name } = useParams<{ name: string }>();
    const isSavedRef = React.useRef(true);
    const { loadedDomains } = useSelector(getDomainsStore);
    const { loadedEmails } = useSelector(getValidEmailsStore);
    const { templatesList } = useSelector(getCheckoutsStore);
    const { status, isTemplateOpen, isEmailTemplateOpen, templatePreview, checkoutObject, templatesSaveSettings } = useSelector(getCheckoutCreatorStore);
    const isGalleryOpen = useSelector(getCheckoutCreatorGalleryOpenStore);
    const {accountSubdomain} = useLandingPageSubdomain()
    const { status: mergeFieldsStatus, data: mergeFields } = useSelector(getMergeFieldsStore);
    const isTemplateEditorReady = mergeFieldsStatus === MergeFieldsStateStatus.LOADED || !!mergeFields;
    const { isLoadingFields, isLoadedFields } = useSelector(getCustomFieldsStore);
    const { isPilotPlan } = useCurrentPlanStatus();
    
    const switchCreatorMode = (): void => { setIsGalleryCreator((value) => !value); };
    const switchCreatorPublicTemplate = (): void => { dispatch(UpdateTemplatesSaveSettings({ isPublic: !templatesSaveSettings?.isPublic })); };
    const switchCreatorDefaultTemplate = (): void => { dispatch(UpdateTemplatesSaveSettings({ isDefault: !templatesSaveSettings?.isDefault })); };
    const switchCreatorEmailDefaultTemplate = (): void => { dispatch(UpdateTemplatesSaveSettings({ isEmailDefault: !templatesSaveSettings?.isEmailDefault })); };
    
    const setCustomScreenshot = (e): void => {
        const file = e.target.files[0];
        if (file && (file.type === 'image/jpeg' || file.type === 'image/png')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const customScreenshotBase64 = reader.result as string;
                dispatch(UpdateTemplatesSaveSettings({ customScreenshot: customScreenshotBase64 })); 
            };
            reader.readAsDataURL(file)
        }
    };

    const historyBackAction = useHistoryBackAction({
        isSavedRef,
        //TODO: no designs and translations yet
        exitModalTitle: t('checkoutexitcreator'),
        exitModalBody: t('checkoutexitcreatorwithoutsaving'),
    });

    const { boot, cleanup, handleExit } = useExitModal(
        isSavedRef,
        CheckoutsPaths.CheckoutsList,
        //TODO: no designs and translations yet
        { title: t('checkoutexitcreator') },
        t('checkoutexitcreatorwithoutsaving')
    );

    useEffect(() => {
        boot();
        dispatch(LoadValidEmails());
        dispatch(LoadDomains());
        dispatch(MergeFieldsLoadStart());
        dispatch(ListsGetNamesStart());
        dispatch(CheckoutsStripeAccountFetch());
        if (!isLoadingFields && !isLoadedFields) {
            dispatch(GetContactFieldsStart({ isUrlUpdateDisabled: true }))
        }
        return () => {
            dispatch(Reset());
            // TODO: check media manager actions and decide if we should make separate components for forms and LPs (currently files are stored in templateEditor store)
            dispatch(TemplateEditorReset())
            cleanup();
        };
    }, []);

    useEffect(()=>{
        if (!checkoutObject?.ProductLanding?.LandingPageID) {
            return;
        }
        //laod accountSubdomain
        !accountSubdomain && dispatch(LoadLandingPageSubdomain());
         
        //load template subdomain
        checkoutObject?.ProductLanding?.LandingPageID && dispatch(LoadLandingPageSubdomain(checkoutObject?.ProductLanding?.LandingPageID));

        if (checkoutObject?.ProductLanding.Template?.TemplateScope === TemplateScope.Public) {
            switchCreatorPublicTemplate();
        }
        if (checkoutObject?.ProductLanding.Template?.IsDefault) {
            switchCreatorDefaultTemplate();
        }
        if (checkoutObject?.PostPurchaseTemplate?.IsDefault) {
            switchCreatorEmailDefaultTemplate();
        }

    },[checkoutObject?.ProductLanding?.LandingPageID ])
    
    useEffect((): void => {
        if (!name) {
            return;
        }
        dispatch(LoadStart(safelyDecodeBase64Url(name)));
    }, []);
    
    useEffect((): void => {
        if (!checkoutObject?.Name) {
            return;
        }
        const url = CheckoutsPaths.CheckoutCreator.replace(`${pathsParams.nameOptional}`, safelyEncodeBase64Url(checkoutObject.Name));
        historyBackAction.updatePathSafely(url);
    }, [checkoutObject]);

    useEffect(() => {
        dispatch(TemplateFormsSetDefaultDoubleOptInDisabled(isPilotPlan));
    }, [isPilotPlan])

    const onExit = (): void => handleExit(null, true)

    const onTemplatePreviewClose = (isManualClose?: boolean): boolean => {
        dispatch(SetTemplatePreview());
        isManualClose && historyBackAction.popBackCallback();
        return true;
    };

    const onEditClick = (): void => {
        const selectedCheckout = templatesList?.find((item) => item.ProductTemplateName === templatePreview?.name);
        dispatch(TemplateOpenFromGallery({
            productTemplateName: templatePreview?.name,
            paymentTemplateName: selectedCheckout.CheckoutTemplateName,
            confirmationTemplateName: selectedCheckout.PaymentConfirmationTemplateName,
            isPublic: true
        }));
        onTemplatePreviewClose(false);
    };

    const steps = useSteps({ onExit, isSavedRef, isGalleryCreator });

    const setGalleryCreatorDefaultTemplates = () => {
        const productTemplate: Template = { ...checkoutObject.ProductLanding.Template, EditorMetaData: DEFAULT_TEMPLATE_EDITOR_META_DATA };
        const paymentTemplate: Template = { ...checkoutObject.CheckoutLanding.Template, EditorMetaData: DEFAULT_TEMPLATE_EDITOR_META_DATA };
        const confirmationTemplate: Template = { ...checkoutObject.PaymentConfirmationLanding.Template, EditorMetaData: DEFAULT_TEMPLATE_EDITOR_META_DATA };
        dispatch(TemplateSetFromGallery(productTemplate,paymentTemplate,confirmationTemplate));
        dispatch(SetIsTemplateOpen(true));
    };

    if (status === CheckoutCreatorStatus.LOADING || !loadedDomains || !loadedEmails || name && status === CheckoutCreatorStatus.DEFAULT) {
        return <Container><SimpleLoader /></Container>;
    }

    return (
        <Container>
            <TabsPanel
                breadcrumbs={t('checkouts')}
                breadcrumbsOnClick={onExit}
                title={t('checkout')}
                disableSentenceCase
            />
            {GALLERY_CREATOR==="true" ? (<div style={{marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '20px'}}>
                <PrimaryButton onClick={switchCreatorMode} text={isGalleryCreator ? 'Editing as creator now' : 'Editing as simple user now'} />
                <PrimaryButton onClick={setGalleryCreatorDefaultTemplates} text="Set empty templates" disabled={!isGalleryCreator} />
                <PrimaryButton onClick={switchCreatorPublicTemplate} text={templatesSaveSettings?.isPublic ? 'saving as public LP templates' : 'saving as private LP templates'} disabled={!isGalleryCreator} />
                <DestructiveButton onClick={switchCreatorDefaultTemplate} text={templatesSaveSettings?.isDefault ? 'saving as default LP templates' : 'saving as non-default LP templates'} disabled={!isGalleryCreator} />
                <DestructiveButton onClick={switchCreatorEmailDefaultTemplate} text={templatesSaveSettings?.isEmailDefault ? 'saving as default Email template' : 'saving as non-default Email template'} disabled={!isGalleryCreator} />
                <div>Override auto screenshot: <input type="file" accept=".jpg,.jpeg,.png" onChange={setCustomScreenshot} /></div>
            </div>) : null}
            <HistoryBackActionContext.Provider value={historyBackAction}>
                <Steps steps={isTemplateEditorReady ? steps : []} activeStep={0} />
                {isTemplateOpen && <TemplateEditorModal isGalleryCreator={isGalleryCreator} />}
                {isEmailTemplateOpen && <EmailTemplateEditorModal isGalleryCreator={isGalleryCreator} />}
                {templatePreview?.content && (
                    <WebsiteModePreviewOverlay fullWidthContent name={templatePreview.name} previewContent={templatePreview.content} onClose={onTemplatePreviewClose} isPreviewEditable={isGalleryOpen} onEdit={onEditClick}/>
                )}
            </HistoryBackActionContext.Provider>
        </Container>
    );
};
